import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Container,
  Form,
  Button,
  ButtonGroup,
  ToggleButton,
  Row,
  Col,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { differenceInDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: right;
`;

const FormGroup = styled(Form.Group)`
  margin-bottom: 1.5rem;
  text-align: right;
`;

const Label = styled(Form.Label)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Result = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background-color: ${({ theme }) => theme.colors.surface.light};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  text-align: center;
`;

const ResultText = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ResultAmount = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: right;
`;

const CurrencyToggle = styled(ButtonGroup)`
  display: flex;
  direction: ltr;
  min-width: 240px;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.primary};
`;

const ToggleOption = styled(ToggleButton)`
  flex: 1;
  padding: 0.5rem 1rem;
  font-size: 1.1rem;
  background-color: ${({ checked, theme }) =>
    checked ? theme.colors.primary : 'transparent'} !important;
  color: ${({ checked, theme }) =>
    checked ? theme.colors.white : theme.colors.primary} !important;
  border: none !important;
  border-left: 1px solid ${({ theme }) => theme.colors.primary} !important;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  margin: 0 !important;
  border-radius: 0 !important;

  &:first-child {
    border-left: none !important;
  }

  &:hover {
    background-color: ${({ checked, theme }) =>
      checked ? theme.colors.primary : `${theme.colors.primary}20`} !important;
    color: ${({ checked, theme }) =>
      checked ? theme.colors.white : theme.colors.primary} !important;
    z-index: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  align-items: stretch;
`;

const CalculateButton = styled(Button)`
  flex: 1;
  font-size: 1.1rem;
  padding: 0.5rem 2rem;
  background-color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  transition: all 0.2s ease-in-out;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
    color: ${({ theme }) => theme.colors.primary} !important;
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: none !important;
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker {
    font-family: inherit;
  }

  .react-datepicker__input-container input {
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #212529;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    text-align: right;
  }
`;

const DaysDisplay = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  font-weight: normal;
  margin-right: 0.5rem;
`;

type SpendingLevel = 'low' | 'medium' | 'high';
type Country =
  | 'thailand'
  | 'japan'
  | 'vietnam'
  | 'korea'
  | 'india'
  | 'cambodia'
  | 'laos'
  | 'philippines'
  | 'indonesia';

interface CountryRates {
  name: string;
  rates: {
    low: number;
    medium: number;
    high: number;
  };
  currency: string;
}

interface FamilyStructure {
  adults: number;
  children: number;
  infants: number;
}

const currencyTranslations: Record<string, string> = {
  THB: 'באט',
  JPY: 'ין',
  VND: 'דונג',
  KRW: 'וון',
  INR: 'רופי',
  USD: 'דולר',
  LAK: 'קיפ',
  PHP: 'פזו',
  IDR: 'רופיה',
};

const countryRates: Record<Country, CountryRates> = {
  thailand: {
    name: 'תאילנד',
    rates: {
      low: 800,
      medium: 1500,
      high: 3000,
    },
    currency: 'THB',
  },
  japan: {
    name: 'יפן',
    rates: {
      low: 6000,
      medium: 12000,
      high: 25000,
    },
    currency: 'JPY',
  },
  vietnam: {
    name: 'וייטנאם',
    rates: {
      low: 600000,
      medium: 1200000,
      high: 2400000,
    },
    currency: 'VND',
  },
  korea: {
    name: 'דרום קוריאה',
    rates: {
      low: 50000,
      medium: 100000,
      high: 200000,
    },
    currency: 'KRW',
  },
  india: {
    name: 'הודו',
    rates: {
      low: 1500,
      medium: 3000,
      high: 6000,
    },
    currency: 'INR',
  },
  cambodia: {
    name: 'קמבודיה',
    rates: {
      low: 40,
      medium: 80,
      high: 150,
    },
    currency: 'USD',
  },
  laos: {
    name: 'לאוס',
    rates: {
      low: 400000,
      medium: 800000,
      high: 1500000,
    },
    currency: 'LAK',
  },
  philippines: {
    name: 'פיליפינים',
    rates: {
      low: 2000,
      medium: 4000,
      high: 8000,
    },
    currency: 'PHP',
  },
  indonesia: {
    name: 'אינדונזיה',
    rates: {
      low: 500000,
      medium: 1000000,
      high: 2000000,
    },
    currency: 'IDR',
  },
};

const ExpensesCalculator = () => {
  const [family, setFamily] = useState<FamilyStructure>({
    adults: 2,
    children: 0,
    infants: 0,
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [spendingLevel, setSpendingLevel] = useState<SpendingLevel>('medium');
  const [country, setCountry] = useState<Country>('thailand');
  const [result, setResult] = useState<number | null>(null);
  const [localCurrencyResult, setLocalCurrencyResult] = useState<number | null>(
    null
  );
  const [selectedCurrency, setSelectedCurrency] = useState<'ILS' | 'local'>(
    'ILS'
  );
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(
    {}
  );
  const [error, setError] = useState<string | null>(null);

  const days = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(endDate, startDate) + 1;
  }, [startDate, endDate]);

  const getSpendingLevelLabel = (level: SpendingLevel) => {
    const amount = countryRates[country].rates[level];
    if (selectedCurrency === 'ILS') {
      const rateInILS =
        amount * (exchangeRates[countryRates[country].currency] || 0);
      return `₪${Math.round(rateInILS).toLocaleString()} ליום`;
    } else {
      return `${amount.toLocaleString()} ${
        currencyTranslations[countryRates[country].currency]
      } ליום`;
    }
  };

  const getDisplayAmount = () => {
    if (!result || !localCurrencyResult) return null;

    if (selectedCurrency === 'ILS') {
      return `₪${result.toLocaleString()}`;
    } else {
      return `${Math.round(localCurrencyResult).toLocaleString()} ${
        currencyTranslations[countryRates[country].currency]
      }`;
    }
  };

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        setError(null);

        const response = await fetch(
          `https://api.freecurrencyapi.com/v1/latest?apikey=${process.env.REACT_APP_CURRENCY_API_KEY}&base_currency=ILS&currencies=THB,JPY,USD,KRW,INR,USD,USD,PHP,IDR`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch exchange rates');
        }

        const data = await response.json();
        const rates: Record<string, number> = {};

        // Convert the rates to ILS base
        Object.entries(data.data).forEach(([currency, rate]) => {
          rates[currency] = 1 / (rate as number);
        });

        // For currencies not directly supported by the API
        if (rates.USD) {
          rates.VND = rates.USD * 0.000041;
          rates.LAK = rates.USD * 0.000051;
        }

        setExchangeRates(rates);
      } catch (err) {
        console.error('Exchange rate error:', err);
        setError('Failed to load exchange rates. Using fallback rates.');
        // Fallback rates
        setExchangeRates({
          THB: 0.11,
          JPY: 0.025,
          VND: 0.00016,
          KRW: 0.003,
          INR: 0.045,
          USD: 3.7,
          LAK: 0.00019,
          PHP: 0.066,
          IDR: 0.00024,
        });
      }
    };

    fetchExchangeRates();
  }, []);

  const calculateExpenses = (e: React.FormEvent) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      setError('נא לבחור תאריכי טיול');
      return;
    }

    const selectedCountry = countryRates[country];
    const dailyRate = selectedCountry.rates[spendingLevel];

    const totalTravelers =
      family.adults + family.children * 0.7 + family.infants * 0.3;

    const totalLocalCurrency = totalTravelers * days * dailyRate;
    const exchangeRate = exchangeRates[selectedCountry.currency] || 0;
    const totalILS = totalLocalCurrency * exchangeRate;

    setLocalCurrencyResult(totalLocalCurrency);
    setResult(Math.round(totalILS));
  };

  return (
    <Container>
      <Title>מחשבון הוצאות טיול</Title>
      {error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}
      <Form onSubmit={calculateExpenses}>
        <FormGroup className="mb-3">
          <Label>יעד</Label>
          <Form.Select
            value={country}
            onChange={(e) => {
              setCountry(e.target.value as Country);
              setResult(null);
              setLocalCurrencyResult(null);
            }}
          >
            {Object.entries(countryRates).map(([key, value]) => (
              <option key={key} value={key}>
                {value.name}
              </option>
            ))}
          </Form.Select>
        </FormGroup>

        <FormGroup className="mb-3">
          <Label>מבנה משפחתי</Label>
          <Row className="g-3">
            <Col xs={12} sm={4}>
              <Form.Group>
                <Form.Label>מבוגרים (גיל 12+)</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="10"
                  value={family.adults}
                  onChange={(e) =>
                    setFamily({
                      ...family,
                      adults: parseInt(e.target.value) || 0,
                    })
                  }
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Group>
                <Form.Label>ילדים (גיל 2-11)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="10"
                  value={family.children}
                  onChange={(e) =>
                    setFamily({
                      ...family,
                      children: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Group>
                <Form.Label>תינוקות (עד גיל 2)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="5"
                  value={family.infants}
                  onChange={(e) =>
                    setFamily({
                      ...family,
                      infants: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </FormGroup>

        <FormGroup className="mb-3">
          <Label>
            תאריכי טיול
            {days > 0 && <DaysDisplay>({days} ימים)</DaysDisplay>}
          </Label>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <DatePickerWrapper>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="תאריך התחלה"
                  minDate={new Date()}
                  required
                />
              </DatePickerWrapper>
            </Col>
            <Col xs={12} sm={6}>
              <DatePickerWrapper>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate || undefined}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="תאריך סיום"
                  required
                />
              </DatePickerWrapper>
            </Col>
          </Row>
        </FormGroup>

        <FormGroup className="mb-3">
          <Label>רמת הוצאה</Label>
          <Form.Select
            value={spendingLevel}
            onChange={(e) => setSpendingLevel(e.target.value as SpendingLevel)}
          >
            <option value="low">חסכוני ({getSpendingLevelLabel('low')})</option>
            <option value="medium">
              בינוני ({getSpendingLevelLabel('medium')})
            </option>
            <option value="high">גבוה ({getSpendingLevelLabel('high')})</option>
          </Form.Select>
        </FormGroup>

        <ButtonContainer>
          <CurrencyToggle>
            <ToggleOption
              id="currency-ils"
              type="radio"
              name="currency"
              value="ILS"
              checked={selectedCurrency === 'ILS'}
              onChange={(e) =>
                setSelectedCurrency(e.currentTarget.value as 'ILS' | 'local')
              }
            >
              שקל (₪)
            </ToggleOption>
            <ToggleOption
              id="currency-local"
              type="radio"
              name="currency"
              value="local"
              checked={selectedCurrency === 'local'}
              onChange={(e) =>
                setSelectedCurrency(e.currentTarget.value as 'ILS' | 'local')
              }
            >
              {countryRates[country].currency}
            </ToggleOption>
          </CurrencyToggle>
          <CalculateButton variant="primary" type="submit">
            חשב הוצאות
          </CalculateButton>
        </ButtonContainer>
      </Form>

      {result !== null && localCurrencyResult !== null && (
        <Result>
          <ResultText>אומדן הוצאות משוער</ResultText>
          <ResultAmount>{getDisplayAmount()}</ResultAmount>
          <InfoText>
            * האומדן כולל לינה, אוכל, תחבורה מקומית ואטרקציות בסיסיות. לא כולל
            טיסות, ביטוח נסיעות והוצאות חריגות.
          </InfoText>
        </Result>
      )}

      <InfoText>
        המחירים מבוססים על ממוצע ההוצאות בכל מדינה. ההוצאות בפועל עשויות להשתנות
        בהתאם לאזור הספציפי, העונה, וסגנון הטיול שלכם.
      </InfoText>
    </Container>
  );
};

export default ExpensesCalculator;
